import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, useManagedTabs } from '@figma/fpl-components';
import { DashboardSection } from '@/components/Dashboard/enums/shared';
import { I18N_KEYS } from '@/language/keys';
import type { DashboardPage } from '@/components/Dashboard/utils/dashboardSection.utils';

/** The three scopes a Workflows or Tools page offers. Workspace is omitted without a team plan. */
type DashboardScope = 'my' | 'workspace' | 'shared';

const SCOPE_SECTION: Record<DashboardScope, DashboardSection> = {
  my: DashboardSection.RecentRecipes,
  workspace: DashboardSection.WorkspaceFiles,
  shared: DashboardSection.SharedWithMe,
};

const SCOPE_ORDER: DashboardScope[] = ['my', 'workspace', 'shared'];

/** Module-level: `useManagedTabs` takes this as a hook dependency. */
const SCOPES_WITH_WORKSPACE: Record<DashboardScope, boolean> = { my: true, workspace: true, shared: true };
const SCOPES_WITHOUT_WORKSPACE: Record<DashboardScope, boolean> = { my: true, workspace: false, shared: true };

const scopeFromSection = (section: DashboardSection): DashboardScope => {
  if (section === DashboardSection.WorkspaceFiles) return 'workspace';
  if (section === DashboardSection.SharedWithMe) return 'shared';
  return 'my';
};

export interface DashboardScopeTabsProps {
  page: DashboardPage;
  section: DashboardSection;
  /** Team and enterprise workspaces. A caller already on Workspace still sees the tab. */
  showWorkspace: boolean;
  onChange: (section: DashboardSection) => void;
}

/**
 * My / Workspace / Shared with me, worded for the page the caller is on. FPL `Tabs` rather than a
 * segmented control, which divides its width equally. Renders only behind `dashboard-tool-tabs`.
 */
export function DashboardScopeTabs({ page, section, showWorkspace, onChange }: DashboardScopeTabsProps) {
  const { t } = useTranslation();
  const scope = scopeFromSection(section);
  const tabIds = showWorkspace || scope === 'workspace' ? SCOPES_WITH_WORKSPACE : SCOPES_WITHOUT_WORKSPACE;

  const labels = useMemo((): Record<DashboardScope, string> => {
    if (page === 'tool') {
      return {
        my: t(I18N_KEYS.DASHBOARD.SCOPE.MY_TOOLS),
        workspace: t(I18N_KEYS.DASHBOARD.SCOPE.WORKSPACE_TOOLS),
        shared: t(I18N_KEYS.DASHBOARD.SCOPE.SHARED_WITH_ME),
      };
    }
    return {
      my: t(I18N_KEYS.DASHBOARD.SCOPE.MY_WORKFLOWS),
      workspace: t(I18N_KEYS.DASHBOARD.SCOPE.WORKSPACE_WORKFLOWS),
      shared: t(I18N_KEYS.DASHBOARD.SCOPE.SHARED_WITH_ME),
    };
  }, [page, t]);

  const handleChange = useCallback((next: DashboardScope) => onChange(SCOPE_SECTION[next]), [onChange]);
  const [tabProps, , tabManager] = useManagedTabs<DashboardScope>(tabIds, scope, handleChange);

  return (
    <Tabs.TabStrip manager={tabManager}>
      {SCOPE_ORDER.filter((tab) => tabIds[tab]).map((tab) => (
        <Tabs.Tab key={tab} {...tabProps[tab]}>
          {labels[tab]}
        </Tabs.Tab>
      ))}
    </Tabs.TabStrip>
  );
}
